import { useEffect } from 'react';

import { toast } from 'react-toastify';

import { useLazyGetTransactionsQuery } from '../services/planHistory.api';
import { useLazyGetToolsQuery } from '../services/psyTools.api';
import {
  useLazyGetActiveTariffsQuery,
  useLazyGetScheduledTariffsQuery,
  useLazyGetTariffsQuery,
} from '../services/tariff.api';
import {
  useLazyGetWalletBalanceQuery,
  useLazyGetWalletTransactionsQuery,
} from '../services/wallet.api';

import { setTools } from '../store/psyTools.slice';
import { setTransaction, setWallet } from '../store/wallet.slice';
import { setActiveTariffs, setScheduledTariffs, setTariffs, setTariffsMaxToolsCount } from '../store/tariff.slice';
import { setPlansHistory } from '../store/planHistory.slice';

import { useAppDispatch } from './redux';

export const useTariffDataUpload = (triggerUpload: boolean, getToolsUnderTrigger?: boolean) => {
  const dispatch = useAppDispatch();
  const [getTools, { data: psyData, error: psyError, isFetching: isPsyLoading }] = useLazyGetToolsQuery();

  const [triggerGetWallet, { data: walletData, error: walletError, isFetching: isWalletLoading }] =
    useLazyGetWalletBalanceQuery();

  const [
    triggerGetTransactions,
    { data: transactionsData, error: transactionsError, isFetching: isTransactionsLoading },
  ] = useLazyGetWalletTransactionsQuery();

  const [triggerGetTariffs, { data: tariffsData, error: tariffsError, isFetching: isTariffsLoading }] =
    useLazyGetTariffsQuery();

  const [
    triggerGetActiveTariffs,
    { data: activeTariffsData, error: activeTariffsError, isFetching: isActiveTariffsLoading },
  ] = useLazyGetActiveTariffsQuery();

  const [
    triggerGetScheduledTariffs,
    { data: scheduledTariffsData, error: scheduledTariffsError, isFetching: isScheduledTariffsLoading },
  ] = useLazyGetScheduledTariffsQuery();

  const [triggerGetPlanHistory, { data: planHistoryData, error: planHistoryError, isFetching: isPlanHistoryLoading }] =
    useLazyGetTransactionsQuery();

  useEffect(() => {
    if (getToolsUnderTrigger && triggerUpload) {
      getTools();
    } else {
      if (!getToolsUnderTrigger) getTools();
    }
  }, [getTools, getToolsUnderTrigger, triggerUpload]);

  useEffect(() => {
    if (triggerUpload) {
      triggerGetWallet();
      triggerGetTransactions();
      triggerGetTariffs();
      triggerGetPlanHistory();
      triggerGetActiveTariffs();
      triggerGetScheduledTariffs();
    }
  }, [
    triggerGetActiveTariffs,
    triggerGetPlanHistory,
    triggerGetScheduledTariffs,
    triggerGetTariffs,
    triggerGetTransactions,
    triggerGetWallet,
    triggerUpload,
  ]);

  useEffect(() => {
    if (psyData) {
      dispatch(setTools(psyData));
    }

    if (walletData) {
      dispatch(setWallet(walletData));
    } else if (walletError) {
      dispatch(setWallet(undefined));
    }
    if (transactionsData?.transactions) {
      dispatch(setTransaction(transactionsData.transactions));
    } else if (transactionsError) {
      dispatch(setTransaction([]));
    }

    if (tariffsData) {
      dispatch(setTariffs(tariffsData.tariffs));
      dispatch(setTariffsMaxToolsCount(tariffsData.maxToolsCount));
    }

    if (planHistoryData) {
      dispatch(setPlansHistory(planHistoryData));
    }

    if (activeTariffsData) {
      dispatch(setActiveTariffs(activeTariffsData));
    }

    if (scheduledTariffsData) {
      dispatch(setScheduledTariffs(scheduledTariffsData));
    }
  }, [
    activeTariffsData,
    dispatch,
    planHistoryData,
    psyData,
    scheduledTariffsData,
    tariffsData,
    transactionsData,
    walletData,
  ]);

  useEffect(() => {
    if (
      walletError ||
      transactionsError ||
      tariffsError ||
      activeTariffsError ||
      scheduledTariffsError ||
      planHistoryError ||
      psyError
    ) {
      toast.error('Ошибка загрузки данных кошелька пользователя');
    }
  }, [
    activeTariffsError,
    planHistoryError,
    psyError,
    scheduledTariffsError,
    tariffsError,
    transactionsError,
    walletError,
  ]);

  const hasWalletData = Boolean(walletData) || Boolean(walletError);
  const hasTransactionsData = Boolean(transactionsData) || Boolean(transactionsError);
  const isDone =
    triggerUpload &&
    !(
      !hasWalletData ||
      !hasTransactionsData ||
      !tariffsData ||
      !activeTariffsData ||
      !scheduledTariffsData ||
      !psyData ||
      !planHistoryData
    );

  const isFetching =
    isWalletLoading ||
    isTransactionsLoading ||
    isPlanHistoryLoading ||
    isTariffsLoading ||
    isActiveTariffsLoading ||
    isScheduledTariffsLoading ||
    isPsyLoading;

  return { isDone, isFetching };
};
